import { Component, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";

class PageDownloadError extends Error {
  constructor(cause) {
    super("Page download failed");
    this.name = "PageDownloadError";
    this.cause = cause;
  }
}

function PageLoading() {
  const { t } = useTranslation();
  return <div role="status" aria-live="polite" className="grid min-h-64 place-items-center p-6 text-muted-foreground">
    {t("common.loading")}
  </div>;
}

function PageDownloadFailed({ onRetry }) {
  const { t } = useTranslation();
  return <section role="alert" className="space-y-4 rounded-xl border p-6">
    <p>{t("pageLoad.error")}</p>
    <button type="button" className="min-h-11 rounded-lg border px-5" onClick={onRetry}>{t("reports.retry")}</button>
    <p className="text-sm text-muted-foreground">{t("pageLoad.reloadHint")}</p>
    <button type="button" className="min-h-11 rounded-lg border px-5" onClick={() => window.location.reload()}>{t("pageLoad.reload")}</button>
  </section>;
}

// Call once at module scope. Successful imports and component identity stay
// cached across renders/navigation. Only an explicit retry replaces a failed
// lazy wrapper, since React also caches a rejected import's promise.
export default function deferredPage(importPage) {
  const makePage = () => lazy(() => Promise.resolve().then(importPage).catch(cause => {
    throw new PageDownloadError(cause);
  }));
  let currentPage = makePage();

  return class DeferredPage extends Component {
    state = { Page: currentPage, error: null };

    static getDerivedStateFromError(error) {
      return { error };
    }

    retry = () => {
      currentPage = makePage();
      this.setState({ Page: currentPage, error: null });
    };

    render() {
      const { Page, error } = this.state;
      // Rendering/API defects must still reach the existing outer boundaries;
      // they are not mislabeled as a failed JavaScript download.
      if (error && !(error instanceof PageDownloadError)) throw error;
      if (error) return <PageDownloadFailed onRetry={this.retry} />;
      // The boundary is inside the existing route wrappers. Loading cannot
      // replace the application shell, reset its drafts or change navigation.
      return <Suspense fallback={<PageLoading />}><Page {...this.props} /></Suspense>;
    }
  };
}
